<template>
  <div>
    <div v-if="video?.files?.length > 0" ref="container" class="relative block">
      <video
        ref="videoPlayer"
        class="aspect-auto w-full object-cover"
        controls
        preload="metadata"
        :poster="video.thumbnail?.getUrl"
        @loadedmetadata="handleVideoMetadataLoaded"
        @timeupdate="handleVideoTimeUpdate"
        @play="isPaused = false"
        @pause="isPaused = true"
        @playing="forceHideProgress = false"
      >
        <source
          :src="
            video.thumbnail
              ? video.files[0].getUrl
              : video.files[0].getUrl + '#t=1'
          "
          type="video/mp4"
        />
        Your browser does not support the video tag.
      </video>
      <div
        class="transition-opacity duration-700 ease-out"
        :class="[
          playerInitialized ? '' : 'hidden',
          shouldHideUI ? ' opacity-0' : 'opacity-100',
        ]"
      >
        <div
          class="absolute top-0 left-0 w-full select-none items-center bg-shade-alpha-900 p-4 backdrop-blur-lg lg:p-6"
        >
          <a
            class="text-right underline text-primary-400"
            :href="`/media/${video.slug}`"
            target="_blank"
            v-if="showLink"
          >
            <p>{{ $t('media.go_media') }}</p>
          </a>
          <h4
            class="flex-auto text-mobile-lg font-semibold text-shade-50 lg:text-lg"
            v-html="video.title"
          />
        </div>
        <button
          class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-400 text-shade-50 lg:h-20 lg:w-20 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
          @click="togglePlay"
          v-if="isPaused"
        >
          <IconPlay class="h-6 w-6 lg:h-14 lg:w-14" />
        </button>

        <!-- Controls -->
        <div
          class="absolute bottom-0 right-0 left-0 p-2 bg-gray-400 bg-opacity-50"
        >
          <div class="flex w-full items-end gap-2 pb-2 px-4 lg:px-10">
            <div class="relative ml-3 flex-1 mt-4">
              <div class="flex items-center">
                <div
                  ref="progressMarker"
                  class="absolute left-0 mt-px -ml-px h-6 w-0.5 bg-shade-50"
                ></div>
                <progress
                  ref="progress"
                  class="h-2 w-full appearance-none bg-shade-alpha-50 webkit-progress-bar:bg-transparent progress-value:bg-primary-400 cursor-pointer"
                  value="0"
                  min="0"
                  :max="progressMax"
                  @click="skipTo"
                />
              </div>
            </div>
          </div>
          <div
            class="flex w-full items-end justify-between gap-2 px-4 lg:px-10"
          >
            <p class="text-shade-50 text-sm">
              {{ formatTime(progress?.value) }}
            </p>
            <p class="text-shade-50 text-sm">
              {{ formatTime(progressMax) }}
            </p>
          </div>
          <div
            class="flex w-full justify-between items-center gap-2 px-4 lg:px-10"
          >
            <div />
            <div />
            <div />
            <div class="flex gap-2">
              <button
                class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-400 text-shade-50 lg:h-10 lg:w-10"
                @click="skipBackward"
              >
                <Rewind class="h-6 w-6 lg:h-6 lg:w-6" />
              </button>
              <button
                class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-400 text-shade-50 lg:h-10 lg:w-10"
                @click="togglePlay"
              >
                <IconPlay v-if="isPaused" class="h-6 w-6 lg:h-6 lg:w-6" />
                <IconPause v-else class="h-6 w-6 lg:h-6 lg:w-6" />
              </button>
              <button
                class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-400 text-shade-50 lg:h-10 lg:w-10"
                @click="skipForward"
              >
                <Forward class="h-6 w-6 lg:h-6 lg:w-6" />
              </button>
            </div>
            <div />
            <div class="flex gap-1">
              <FeedbackPopover position="top-center">
                <template #button>
                  <button
                    class="group relative flex h-14 w-14 items-center justify-center disabled:cursor-not-allowed disabled:opacity-50 text-white"
                  >
                    {{ playbackRate.toString() }}x
                    <div
                      class="absolute h-full w-full scale-75 bg-shade-alpha-50 opacity-0 transition-all duration-200 ease-in-out group-hover:scale-100 group-hover:opacity-100"
                    ></div>
                  </button>
                </template>
                <div class="flex gap-1">
                  <span
                    v-for="rate in playbackRates"
                    @click="() => (playbackRate = rate)"
                    class="hover:text-primary-400 cursor-pointer"
                    :class="{
                      'text-primary-400':
                        rate.toString() === playbackRate.toString(),
                      'text-black': rate.toString() !== playbackRate.toString(),
                    }"
                  >
                    {{ rate }}x
                  </span>
                </div>
              </FeedbackPopover>

              <FeedbackPopover position="top-center">
                <template #button>
                  <button
                    class="group relative flex h-14 w-14 items-center justify-center disabled:cursor-not-allowed disabled:opacity-50 text-white"
                  >
                    <IconVolume class="z-10 h-8 w-8 fill-white" />
                    <div
                      class="absolute h-full w-full scale-75 bg-shade-alpha-50 opacity-0 transition-all duration-200 ease-in-out group-hover:scale-100 group-hover:opacity-100"
                    ></div>
                  </button>
                </template>
                <div class="inline-block h-48 w-2 p-0 leading-none">
                  <div class="w-6">
                    <BasicSlider
                      v-model="currentVolume"
                      min="0"
                      max="1"
                      step="0.01"
                      class="align-text-top"
                    />
                  </div>
                </div>
              </FeedbackPopover>
              <div name="button">
                <button
                  class="group relative flex h-14 w-14 items-center justify-center disabled:cursor-not-allowed disabled:opacity-50 text-white"
                >
                  <IconFullScreen
                    class="fill-white cursor-pointer ml-2"
                    @click="toggleFullScreen"
                  />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { PropType } from 'vue'
import { useMouseInElement, useIdle, useElementHover } from '@vueuse/core'
import IconPlay from '~/assets/icon/media/Play-Alt.svg'
import IconPause from '~/assets/icon/media/Pause.svg'
import IconFullScreen from '~/assets/icon/media/fullscreen.svg'
import Rewind from '~/assets/icon/media/Rewind.svg'
import Forward from '~/assets/icon/media/Forward.svg'
import IconVolume from '~/assets/icon/media/Volume.svg'

const props = defineProps({
  video: {
    type: Object as PropType<Media>,
    required: true,
  },
  showLink: {
    type: Boolean,
    default: false,
  },
})

const currentPlayingMediaId = useState('currentPlayingMediaId')

const container = ref(null)
const videoPlayer = ref(null)
const progress = ref(null)
const progressMarker = ref(null)
const playerInitialized = ref(false)
const progressMax = ref(0)
const isPaused = ref(true)
const forceHideProgress = ref(true)
const playbackRate = ref(1)
const playbackRates = [1, 1.2, 1.5, 2]
const currentVolume = ref(1)

const { elementX: progressX } = useMouseInElement(progress)
const { idle } = useIdle(1500)
const isHovered = useElementHover(container)

const shouldHideUI = computed(() => {
  if (isHovered.value) {
    return idle.value && !isPaused.value
  }
  return !isPaused.value
})

const toggleFullScreen = () => {
  const elem = container.value
  if (document.fullscreenElement) {
    document.exitFullscreen()
  } else {
    elem.requestFullscreen().catch((err) => {
      console.error('Error attempting to enable full-screen mode:', err.message)
    })
  }
}

const formatTime = (timeInSeconds: number) => {
  const minutes = Math.floor(timeInSeconds / 60)
  const seconds = Math.floor(timeInSeconds % 60)
  return `${minutes.toString().padStart(2, '0')}:${seconds
    .toString()
    .padStart(2, '0')}`
}

watch(currentPlayingMediaId, (newId) => {
  if (newId !== props.video?.id) {
    videoPlayer.value.pause()
  }
})
const togglePlay = () => {
  if (videoPlayer.value?.paused || videoPlayer.value?.ended) {
    currentPlayingMediaId.value = props.video?.id
    videoPlayer.value.play()
  } else {
    if (currentPlayingMediaId.value === props.video?.id) {
      currentPlayingMediaId.value = null
    }
    videoPlayer.value.pause()
  }
}

const configurePlayer = () => {
  videoPlayer.value.controls = false
  playerInitialized.value = true
}

const handleVideoMetadataLoaded = () => {
  progressMax.value = videoPlayer.value.duration
}

const handleVideoTimeUpdate = () => {
  if (!progressMax.value) {
    progressMax.value = videoPlayer.value.duration
  }
  progress.value.value = videoPlayer.value.currentTime
  progressMarker.value.style.left =
    (videoPlayer.value.currentTime / videoPlayer.value.duration) * 100 + '%'
}

const skipTo = (e) => {
  videoPlayer.value.currentTime =
    (progressX.value / progress.value.offsetWidth) * videoPlayer.value.duration
}

const skipForward = () => {
  videoPlayer.value.currentTime = Math.min(
    videoPlayer.value.duration,
    videoPlayer.value.currentTime + 10
  )
}

const skipBackward = () => {
  videoPlayer.value.currentTime = Math.max(
    0,
    videoPlayer.value.currentTime - 10
  )
}

watch(
  () => playbackRate.value,
  (newValue) => {
    videoPlayer.value.playbackRate = newValue
  }
)

const duration = computed<string>(() => {
  return props.video.duration.length
})

watch(currentVolume, (newVolume) => {
  videoPlayer.value.volume = newVolume
  if (newVolume <= 0) {
    videoPlayer.value.muted = true
  } else {
    videoPlayer.value.muted = false
  }
})
onMounted(() => {
  configurePlayer()
  videoPlayer.value.playbackRate = playbackRate.value
})
</script>
